import React, { useState, useEffect, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

// Admin password - Change this to your friend's password
const ADMIN_PASSWORD = 'Admin2025';

interface CustomerInquiry {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  service_type: string | null;
  message: string | null;
  inquiry_type: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface PortfolioImage {
  id: string;
  category: string;
  image_url: string;
  storage_path: string;
  created_at: string;
}

const PORTFOLIO_CATEGORIES = [
  { id: 'interior', title: 'Interior Painting', icon: 'ri-home-4-line' },
  { id: 'exterior', title: 'Exterior Painting', icon: 'ri-building-2-line' },
  { id: 'spray', title: 'Spray Jobs', icon: 'ri-spray-line' },
  { id: 'flooring', title: 'Flooring', icon: 'ri-layout-grid-line' }
];

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState<'inquiries' | 'portfolio'>('inquiries');
  const [inquiries, setInquiries] = useState<CustomerInquiry[]>([]);
  const [portfolioImages, setPortfolioImages] = useState<PortfolioImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<CustomerInquiry | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('interior');
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAuthenticated) {
      loadInquiries();
      loadPortfolioImages();
      
      // Set up real-time subscription for inquiries
      const inquiriesSubscription = supabase
        .channel('customer_inquiries_changes')
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'customer_inquiries' },
          () => {
            loadInquiries();
          }
        )
        .subscribe();

      // Set up real-time subscription for portfolio images
      const portfolioSubscription = supabase
        .channel('portfolio_images_changes')
        .on('postgres_changes', 
          { event: '*', schema: 'public', table: 'portfolio_images' },
          () => {
            loadPortfolioImages();
          }
        )
        .subscribe();

      return () => {
        inquiriesSubscription.unsubscribe();
        portfolioSubscription.unsubscribe();
      };
    }
  }, [isAuthenticated]);

  const loadInquiries = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('customer_inquiries')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setInquiries(data || []);
    } catch (error) {
      console.error('Error loading inquiries:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPortfolioImages = async () => {
    try {
      const { data, error } = await supabase
        .from('portfolio_images')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPortfolioImages(data || []);
    } catch (error) {
      console.error('Error loading portfolio images:', error);
    }
  };

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setPassword('');
    } else {
      alert('Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('customer_inquiries')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
      
      await loadInquiries();
      
      if (selectedInquiry?.id === id) {
        setSelectedInquiry({ ...selectedInquiry, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    }
  };

  const handleDeleteInquiry = async (id: string) => {
    if (!confirm('Are you sure you want to delete this inquiry?')) return;

    try {
      const { error } = await supabase
        .from('customer_inquiries')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      await loadInquiries();
      setSelectedInquiry(null);
    } catch (error) {
      console.error('Error deleting inquiry:', error);
      alert('Failed to delete inquiry');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);

    try {
      for (const file of Array.from(files)) {
        // Generate unique filename
        const fileExt = file.name.split('.').pop();
        const fileName = `${selectedCategory}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        // Upload to Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('portfolio-images')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('portfolio-images')
          .getPublicUrl(fileName);

        // Save metadata to database
        const { error: dbError } = await supabase
          .from('portfolio_images')
          .insert({
            category: selectedCategory,
            image_url: publicUrl,
            storage_path: fileName
          });

        if (dbError) throw dbError;
      }

      // Reload images
      await loadPortfolioImages();
      setShowUploadModal(false);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      alert('Images uploaded successfully!');
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (image: PortfolioImage) => {
    if (!confirm('Are you sure you want to delete this image?')) return;

    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('portfolio-images')
        .remove([image.storage_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('portfolio_images')
        .delete()
        .eq('id', image.id);

      if (dbError) throw dbError;

      // Reload images
      await loadPortfolioImages();
      
      alert('Image deleted successfully!');
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image. Please try again.');
    }
  };

  const filteredInquiries = inquiries.filter(inquiry => {
    const matchesStatus = filterStatus === 'all' || inquiry.status === filterStatus;
    const matchesType = filterType === 'all' || inquiry.inquiry_type === filterType;
    const matchesSearch = searchTerm === '' || 
      inquiry.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inquiry.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inquiry.phone && inquiry.phone.includes(searchTerm));
    
    return matchesStatus && matchesType && matchesSearch;
  });

  const categoryImages = portfolioImages.filter(img => img.category === selectedCategory);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'in-progress': return 'bg-yellow-100 text-yellow-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'quote': return 'ri-file-text-line';
      case 'contact': return 'ri-mail-line';
      case 'appointment': return 'ri-calendar-line';
      default: return 'ri-message-3-line';
    }
  };

  const stats = {
    total: inquiries.length,
    new: inquiries.filter(i => i.status === 'new').length,
    inProgress: inquiries.filter(i => i.status === 'in-progress').length,
    completed: inquiries.filter(i => i.status === 'completed').length,
  };

  const portfolioStats = PORTFOLIO_CATEGORIES.map(cat => ({
    ...cat,
    count: portfolioImages.filter(img => img.category === cat.id).length
  }));

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-keyhole-line text-4xl text-teal-600"></i>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Enter password to access admin panel</p>
          </div>

          <div className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
              placeholder="Enter admin password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-base"
              autoFocus
            />

            <button
              onClick={handleLogin}
              className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold whitespace-nowrap"
            >
              <i className="ri-login-box-line mr-2"></i>
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-dashboard-line text-xl text-white"></i>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Manage inquiries and portfolio</p>
              </div>
            </div>
            <button
              onClick={() => setIsAuthenticated(false)}
              className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors whitespace-nowrap"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('inquiries')}
              className={`px-6 py-4 font-semibold transition-all duration-300 border-b-2 whitespace-nowrap ${
                activeTab === 'inquiries'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-inbox-line mr-2"></i>
              Customer Inquiries
            </button>
            <button
              onClick={() => setActiveTab('portfolio')}
              className={`px-6 py-4 font-semibold transition-all duration-300 border-b-2 whitespace-nowrap ${
                activeTab === 'portfolio'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <i className="ri-gallery-line mr-2"></i>
              Portfolio Management
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'inquiries' ? (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Total Inquiries</p>
                    <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
                  </div>
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <i className="ri-inbox-line text-2xl text-gray-600"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">New</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.new}</p>
                  </div>
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className="ri-notification-badge-line text-2xl text-blue-600"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">In Progress</p>
                    <p className="text-3xl font-bold text-yellow-600">{stats.inProgress}</p>
                  </div>
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-yellow-600"></i>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Completed</p>
                    <p className="text-3xl font-bold text-green-600">{stats.completed}</p>
                  </div>
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Search</label>
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search by name, email, or phone"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="new">New</option>
                    <option value="in-progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="quote">Quote Request</option>
                    <option value="contact">Contact Form</option>
                    <option value="appointment">Appointment</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Inquiries List */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* List */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="p-6 border-b border-gray-200">
                  <h2 className="text-lg font-bold text-gray-900">
                    Inquiries ({filteredInquiries.length})
                  </h2>
                </div>
                
                <div className="overflow-y-auto" style={{ maxHeight: '600px' }}>
                  {loading ? (
                    <div className="p-8 text-center">
                      <div className="inline-block w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                      <p className="mt-4 text-gray-600">Loading inquiries...</p>
                    </div>
                  ) : filteredInquiries.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-inbox-line text-3xl text-gray-400"></i>
                      </div>
                      <p className="text-gray-600">No inquiries found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredInquiries.map((inquiry) => (
                        <div
                          key={inquiry.id}
                          onClick={() => setSelectedInquiry(inquiry)}
                          className={`p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
                            selectedInquiry?.id === inquiry.id ? 'bg-teal-50' : ''
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center mr-3">
                                <i className={`${getTypeIcon(inquiry.inquiry_type)} text-lg text-teal-600`}></i>
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">{inquiry.name}</h3>
                                <p className="text-sm text-gray-600">{inquiry.email}</p>
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(inquiry.status)}`}>
                              {inquiry.status}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 ml-13">
                            {new Date(inquiry.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Detail View */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {selectedInquiry ? (
                  <>
                    <div className="p-6 border-b border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-gray-900">Inquiry Details</h2>
                        <button
                          onClick={() => handleDeleteInquiry(selectedInquiry.id)}
                          className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete inquiry"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>

                      <div className="flex items-center mb-4">
                        <label className="text-sm font-semibold text-gray-700 mr-3">Status:</label>
                        <select
                          value={selectedInquiry.status}
                          onChange={(e) => handleStatusChange(selectedInquiry.id, e.target.value)}
                          className="px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        >
                          <option value="new">New</option>
                          <option value="in-progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </div>

                    <div className="p-6 space-y-6 overflow-y-auto" style={{ maxHeight: '500px' }}>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <i className="ri-user-line mr-2"></i>Name
                        </label>
                        <p className="text-gray-900">{selectedInquiry.name}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <i className="ri-mail-line mr-2"></i>Email
                        </label>
                        <a href={`mailto:${selectedInquiry.email}`} className="text-teal-600 hover:underline">
                          {selectedInquiry.email}
                        </a>
                      </div>

                      {selectedInquiry.phone && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <i className="ri-phone-line mr-2"></i>Phone
                          </label>
                          <a href={`tel:${selectedInquiry.phone}`} className="text-teal-600 hover:underline">
                            {selectedInquiry.phone}
                          </a>
                        </div>
                      )}

                      {selectedInquiry.service_type && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <i className="ri-service-line mr-2"></i>Service Type
                          </label>
                          <p className="text-gray-900">{selectedInquiry.service_type}</p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <i className="ri-file-list-line mr-2"></i>Inquiry Type
                        </label>
                        <p className="text-gray-900 capitalize">{selectedInquiry.inquiry_type}</p>
                      </div>

                      {selectedInquiry.message && (
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            <i className="ri-message-3-line mr-2"></i>Message
                          </label>
                          <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                            {selectedInquiry.message}
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <i className="ri-time-line mr-2"></i>Received
                        </label>
                        <p className="text-gray-900">
                          {new Date(selectedInquiry.created_at).toLocaleString()}
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          <i className="ri-refresh-line mr-2"></i>Last Updated
                        </label>
                        <p className="text-gray-900">
                          {new Date(selectedInquiry.updated_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="p-8 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-file-list-line text-3xl text-gray-400"></i>
                    </div>
                    <p className="text-gray-600">Select an inquiry to view details</p>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Portfolio Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              {portfolioStats.map((cat) => (
                <div key={cat.id} className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{cat.title}</p>
                      <p className="text-3xl font-bold text-gray-900">{cat.count}</p>
                    </div>
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className={`${cat.icon} text-2xl text-teal-600`}></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Category Selection & Upload */}
            <div className="bg-white rounded-xl p-6 mb-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-900">Portfolio Management</h2>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-300 shadow-md whitespace-nowrap"
                >
                  <i className="ri-upload-2-line mr-2"></i>
                  Upload Photos
                </button>
              </div>

              <div className="flex flex-wrap gap-3">
                {PORTFOLIO_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`flex items-center px-4 py-2 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                      selectedCategory === cat.id
                        ? 'bg-teal-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-2`}></i>
                    {cat.title}
                  </button>
                ))}
              </div>
            </div>

            {/* Portfolio Images Grid */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-6">
                {PORTFOLIO_CATEGORIES.find(c => c.id === selectedCategory)?.title} ({categoryImages.length})
              </h3>

              {categoryImages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-image-line text-3xl text-gray-400"></i>
                  </div>
                  <p className="text-gray-600 mb-4">No images in this category yet</p>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-300 whitespace-nowrap"
                  >
                    <i className="ri-upload-2-line mr-2"></i>
                    Upload First Photo
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {categoryImages.map((image) => (
                    <div key={image.id} className="relative group">
                      <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                        <img
                          src={image.image_url}
                          alt={selectedCategory}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                      </div>
                      <button
                        onClick={() => handleDeleteImage(image)}
                        className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-red-700"
                        title="Delete image"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                      <div className="absolute bottom-2 left-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        {new Date(image.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-gray-900">Upload Photos</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                disabled={uploading}
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-3">Select Category</label>
              <div className="grid grid-cols-2 gap-3">
                {PORTFOLIO_CATEGORIES.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    disabled={uploading}
                    className={`flex items-center justify-center px-4 py-3 rounded-lg font-semibold transition-all duration-300 whitespace-nowrap ${
                      selectedCategory === cat.id
                        ? 'bg-teal-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${cat.icon} mr-2`}></i>
                    <span className="text-sm">{cat.title.split(' ')[0]}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-teal-500 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      Uploading...
                    </p>
                    <p className="text-sm text-gray-500">
                      Please wait while we upload your images
                    </p>
                  </>
                ) : (
                  <>
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-image-add-line text-3xl text-teal-600"></i>
                    </div>
                    <p className="text-lg font-semibold text-gray-900 mb-2">
                      Click to upload photos
                    </p>
                    <p className="text-sm text-gray-500 mb-2">
                      Upload to: <strong>{PORTFOLIO_CATEGORIES.find(c => c.id === selectedCategory)?.title}</strong>
                    </p>
                    <p className="text-xs text-gray-400">
                      Select multiple images from your device
                    </p>
                  </>
                )}
              </button>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={() => setShowUploadModal(false)}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                disabled={uploading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
